import React from 'react';
import { isEqual } from 'lodash';

import UserDevice from 'services/user-device';
import TwitchPlayContainer from '../TwitchPlayContainer';

import './twitchEmbedVideo.scss';

const PLAYER_URL = '//player.twitch.tv/js/embed/v1.js';

interface Props {
    playerNodeId: string;
    channel?: string;
    autoplay?: boolean;
    onReady?: () => void;
    onPlay?: () => void;
    onPause?: () => void;
    onEnd?: () => void;
    onOffline?: () => void;
    onOnline?: () => void;
    initPlayer: (player: TwitchPlayer | null) => void;
    isSmallMedium: boolean;
}

interface State {
    isPlayerAvailable: boolean;
    isPlaying: boolean;
}

class TwitchEmbedVideo extends React.Component<Props, State> {
    player: TwitchPlayer | null = null;

    state: State = { isPlayerAvailable: false, isPlaying: false };

    componentDidMount() {
        const twitchEmbedScript = document.querySelector(
            '#twitch-embed-script'
        );

        if (twitchEmbedScript) {
            this.initPlayer();

            return;
        }

        const script = document.createElement('script');
        script.setAttribute('src', PLAYER_URL);
        script.setAttribute('id', 'twitch-embed-script');

        document.body.appendChild(script);

        script.addEventListener('load', () => {
            this.initPlayer();
        });
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (!isEqual(nextProps, this.props)) {
            if (this.player && nextProps.channel) {
                this.player.setChannel(nextProps.channel);
            }
        }
    }

    componentWillUnmount() {
        this.props.initPlayer(null);
    }

    initPlayer = (): void => {
        const { playerNodeId, channel, autoplay, isSmallMedium } = this.props;

        if (!window.Twitch || !window.Twitch.Player) {
            console.warn(`${PLAYER_URL} not loaded`);

            return;
        }

        if (!channel) {
            console.warn('Twitch channel is missing');

            return;
        }

        this.player = new window.Twitch.Player(playerNodeId, {
            width: '100%',
            height: '100%',
            channel,
            layout: 'video',
            autoplay: !!autoplay,
            playsinline: true,
            muted: true,
            allowfullscreen: !isSmallMedium,
        });

        this.addPlayerEventListeners(this.player);

        if (this.player) {
            this.props.initPlayer(this.player);
        }

        this.setState({ isPlayerAvailable: true });
    };

    private addPlayerEventListeners = (player: TwitchPlayer) => {
        if (!window.Twitch) return;

        player.addEventListener(window.Twitch.Player.PAUSE, () => {
            this.setState({ isPlaying: false });

            if (this.props.onPause) {
                this.props.onPause();
            }
        });

        player.addEventListener(window.Twitch.Player.ENDED, () => {
            if (this.props.onEnd) {
                this.props.onEnd();
            }
        });

        player.addEventListener(window.Twitch.Player.PLAY, () => {
            this.setState({ isPlaying: true });

            if (this.props.onPlay) {
                this.props.onPlay();
            }
        });

        player.addEventListener(window.Twitch.Player.READY, () => {
            if (this.props.onReady) {
                this.props.onReady();
            }
        });

        player.addEventListener(window.Twitch.Player.OFFLINE, () => {
            if (this.props.onOffline) {
                this.props.onOffline();
            }
        });

        player.addEventListener(window.Twitch.Player.ONLINE, () => {
            if (this.props.onOnline) {
                this.props.onOnline();
            }
        });
    };

    render() {
        const { isPlayerAvailable, isPlaying } = this.state;

        return (
            <React.Fragment>
                {UserDevice.getInstance()?.isDeviceMobile() &&
                    this.player &&
                    isPlayerAvailable &&
                    !isPlaying && (
                        <TwitchPlayContainer onPlay={this.player?.play} />
                    )}

                <div id={this.props.playerNodeId} styleName="twitch-embed" />
            </React.Fragment>
        );
    }
}

export default TwitchEmbedVideo;
